import React, { useEffect, useMemo } from 'react'
import NextImage from 'next/image'
import { analytics } from '@helpers/analytics'
import { getLink } from '@helpers/env'
import { resizeShopifyImage } from '@helpers/shopify'
import PriceComparison from '@components/ui/PriceComparison'
import useLocalisedPrice from '@helpers/hooks/useLocalisedPrice'
import {
  CompleteTheLookProduct,
  CompleteTheLookProductsByHandle
} from '@lib/completeTheLook'
import styles from './styles.module.scss'

type CompleteTheLookItemProps = {
  product: CompleteTheLookProduct
  sourceHandle: string
  rank: number
}

const CompleteTheLookItem = ({
  product,
  sourceHandle,
  rank
}: CompleteTheLookItemProps) => {
  const link = getLink(`/products/${product.handle}`)
  const { price, fullPrice, currency, isLoading } = useLocalisedPrice(
    product.id,
    product.price,
    product.compare_at_price
  )

  return (
    <a
      className={styles.product}
      href={link}
      onClick={() =>
        analytics('Complete The Look Clicked', {
          source_handle: sourceHandle,
          target_handle: product.handle,
          rank,
          placement: 'PDP'
        })
      }
    >
      <NextImage
        className={styles.image}
        src={resizeShopifyImage(product.image.src, {
          width: '242',
          height: '332',
          crop: 'center'
        })}
        alt={`${product.display_name} ${product.title}`}
        width="242"
        height="332"
      />
      <div className={styles.details}>
        <div className={styles.titleRow}>
          <h3 className={styles.name}>
            {product.display_name || product.product_type}
          </h3>
          <span className={styles.price}>
            <PriceComparison
              price={price}
              fullPrice={fullPrice}
              currency={currency}
              isLoading={isLoading}
              size="small"
            />
          </span>
        </div>
        <p className={styles.variant}>{product.title}</p>
      </div>
    </a>
  )
}

type CompleteTheLookProps = {
  sourceHandle: string
  productsByHandle: CompleteTheLookProductsByHandle
}

const CompleteTheLook = ({
  sourceHandle,
  productsByHandle
}: CompleteTheLookProps) => {
  const visibleProducts = useMemo(
    () => (productsByHandle[sourceHandle] || []).slice(0, 2),
    [productsByHandle, sourceHandle]
  )

  useEffect(() => {
    if (!visibleProducts.length) return

    analytics('Complete The Look Viewed', {
      source_handle: sourceHandle,
      target_handles: visibleProducts.map(product => product.handle),
      placement: 'PDP'
    })
  }, [visibleProducts, sourceHandle])

  if (!visibleProducts.length) return null

  return (
    <section
      className={styles.completeTheLook}
      aria-labelledby="complete-look-heading"
    >
      <h2 id="complete-look-heading" className={styles.heading}>
        Mix &amp; Match
      </h2>
      <div className={styles.products}>
        {visibleProducts.map((product, index) => (
          <CompleteTheLookItem
            key={product.handle}
            product={product}
            sourceHandle={sourceHandle}
            rank={index + 1}
          />
        ))}
      </div>
    </section>
  )
}

export default CompleteTheLook
